import { useNavigate, useSearchParams } from "@solidjs/router";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { createMemo, createSignal, onCleanup, onMount, Show } from "solid-js";
import type { MapPreviewView } from "../../enums/map";
import { MAP_CHANGE_STATUS, MAP_PREVIEW_VIEW } from "../../enums/map";
import { setTransientMapBrief } from "../brief/briefTransient";
import { ModuleShell } from "../scan/ModuleShell";
import { ScanRouteHeader } from "../scan/ScanRouteHeader";
import { createWebsiteStore } from "../website/store";
import { MapAdjustmentSheet } from "./MapAdjustmentSheet";
import { MapChangeQueue } from "./MapChangeQueue";
import { MapCurrentBaseline } from "./MapCurrentBaseline";
import { MapMigrationPlan } from "./MapMigrationPlan";
import { MapPlanStatus } from "./MapPlanStatus";
import { MapPreviewNav } from "./MapPreviewNav";
import { MapScanNavigation } from "./MapScanNavigation";
import { MapSelectedChange } from "./MapSelectedChange";
import { MapTargetEstate } from "./MapTargetEstate";
import { buildMapBriefHandoff } from "./mapBriefHandoff";
import { applyMapEdits, buildMapPlan, buildTargetEstate } from "./mapModel";
import {
  buildMapPreviewActions,
  type MapPreviewAction,
} from "./mapPreviewActions";
import { buildMapPreview, mapPreviewView } from "./mapPreviewModel";
import type { MapChangeEdit } from "./mapTypes";

export function ScanMap(props: { websiteId: string }) {
  const context = createWebsiteStore(props.websiteId);
  return (
    <ModuleShell
      context={context}
      activeModule="map"
      workspaceClass="signal-workspace signal-map-workspace"
    >
      <Show
        when={context.data()}
        keyed
        fallback={
          <div class="loading signal-loading">
            Loading Signal Map preview...
          </div>
        }
      >
        {(data) => (
          <MapPreviewWorkspace data={data} websiteId={props.websiteId} />
        )}
      </Show>
    </ModuleShell>
  );
}

function MapPreviewWorkspace(props: {
  data: OverviewResponse;
  websiteId: string;
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const plan = createMemo(() => buildMapPlan(props.data));
  const preview = createMemo(() => buildMapPreview(plan()));
  const [edits, setEdits] = createSignal<Record<string, MapChangeEdit>>({});
  const [selectedId, setSelectedId] = createSignal<string | null>(
    buildMapPreviewActions(preview(), plan().changes)[0]?.change.id ?? null,
  );
  const [adjusting, setAdjusting] = createSignal(false);
  const [sheetTrigger, setSheetTrigger] = createSignal<HTMLElement | null>(
    null,
  );
  const view = createMemo<MapPreviewView>(() => mapPreviewView(search.mapView));
  const changes = createMemo(() => applyMapEdits(plan().changes, edits()));
  const actions = createMemo(() =>
    buildMapPreviewActions(preview(), changes()),
  );
  const selectedAction = createMemo(
    () => actions().find((action) => action.change.id === selectedId()) ?? null,
  );
  const targetItems = createMemo(() =>
    buildTargetEstate(
      plan(),
      actions().map((action) => action.change),
    ),
  );
  const updateAction = (action: MapPreviewAction, patch: MapChangeEdit) =>
    setEdits((current) => {
      const next = { ...current };
      for (const id of action.sourceChangeIds) {
        next[id] = { ...(next[id] ?? {}), ...patch };
      }
      return next;
    });
  const reset = () => {
    setEdits({});
    setSelectedId(
      buildMapPreviewActions(preview(), plan().changes)[0]?.change.id ?? null,
    );
    setAdjusting(false);
  };
  const setView = (next: MapPreviewView) =>
    setSearch({
      mapView: next === MAP_PREVIEW_VIEW.BASELINE ? undefined : next,
    });
  const reviewAction = (id: string) => {
    setSelectedId(id);
    setView(MAP_PREVIEW_VIEW.ARCHITECTURE);
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && !adjusting() && selectedId()) {
      setSelectedId(null);
    }
  };
  onMount(() => window.addEventListener("keydown", onKeyDown));
  onCleanup(() => window.removeEventListener("keydown", onKeyDown));

  return (
    <div class="signal-route-surface signal-map-plan map-preview-workspace">
      <ScanRouteHeader
        websiteId={props.websiteId}
        section="Map"
        title="Signal Map"
        subtitle="Explore draft recommendations for the content structure, using the whole scanned estate for this website."
        actions={<MapScanNavigation websiteId={props.websiteId} />}
      />
      <MapPreviewNav view={view()} onChange={setView} />

      <Show when={view() === MAP_PREVIEW_VIEW.BASELINE}>
        <MapCurrentBaseline
          websiteId={props.websiteId}
          plan={plan()}
          preview={preview()}
        />
      </Show>

      <Show when={view() === MAP_PREVIEW_VIEW.ARCHITECTURE}>
        <div class="map-architecture-view">
          <MapPlanStatus
            actions={actions()}
            dirty={Object.keys(edits()).length > 0}
            onReset={reset}
          />
          <Show when={plan().emptyReason} keyed>
            {(reason) => (
              <section class="map-limited-state">
                <span class="signal-mini-kicker">
                  Limited structural evidence
                </span>
                <strong>{reason}</strong>
              </section>
            )}
          </Show>
          <MapChangeQueue
            actions={actions()}
            selectedId={selectedId()}
            emptyReason={plan().emptyReason}
            onSelect={setSelectedId}
          />
          <div class="map-architecture-detail-grid">
            <MapSelectedChange
              data={props.data}
              websiteId={props.websiteId}
              action={selectedAction()}
              source={preview().source}
              onAccept={() => {
                const action = selectedAction();
                if (action)
                  updateAction(action, { status: MAP_CHANGE_STATUS.ACCEPTED });
              }}
              onUnresolved={() => {
                const action = selectedAction();
                if (action)
                  updateAction(action, {
                    status: MAP_CHANGE_STATUS.UNRESOLVED,
                  });
              }}
              onDismiss={() => {
                const action = selectedAction();
                if (action)
                  updateAction(action, { status: MAP_CHANGE_STATUS.DISMISSED });
              }}
              onAdjust={(trigger) => {
                setSheetTrigger(trigger);
                setAdjusting(true);
              }}
              onBrief={() => {
                const change = selectedAction()?.change;
                if (change?.status !== MAP_CHANGE_STATUS.ACCEPTED) return;
                setTransientMapBrief(
                  props.websiteId,
                  buildMapBriefHandoff(change),
                );
                navigate(`/w/${props.websiteId}/brief?source=map`);
              }}
            />
            <MapTargetEstate
              items={targetItems()}
              vocabulary={plan().vocabulary}
              selectedId={selectedId()}
              onSelect={setSelectedId}
            />
          </div>
        </div>
      </Show>

      <Show when={view() === MAP_PREVIEW_VIEW.MIGRATION}>
        <MapMigrationPlan actions={actions()} onReview={reviewAction} />
      </Show>

      <Show when={adjusting() && selectedAction()} keyed>
        {(action) => (
          <MapAdjustmentSheet
            change={action.change}
            returnFocus={sheetTrigger()}
            onClose={() => setAdjusting(false)}
            onApply={(patch) => {
              updateAction(action, patch);
              setAdjusting(false);
            }}
          />
        )}
      </Show>
    </div>
  );
}
